import Loader from '@/components/common/Loader';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import { FxOptionPositionData, SummaryResponse } from '@/types/sso';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalOptionPositionTable from '@/utils/hooks/usePortalOptionPositionTable';
import { memo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import SafeInput from '../inputs/SafeInput';
import PortalVirtualizedTable from '../table/PortalVirtualizedTable';

export interface PortalOptionPositionRow extends FxOptionPositionData {
    tableType: string;
    tableSubType: string;
    tablePrice: string;
    tableStrikePrice: string;
    tableAmount: string;
    tableLowerBarrier: string;
    tableUpperBarrier: string;
    tableSymbol: string;
    premium: number;
    baseAssetName: string;
    quoteAssetName: string;
    tradeStatus: string;
    tableTradeDate: string;
    tableExpiryDate: string;
    tableCurrentPrice: string;
    tableCurrentValue: string;
    tableCurrentValueInBaseCurrency: string;
}

export interface PortalOptionPositionTableProps {
    summary?: SummaryResponse;
    baseCurrency: string;
    isHistoric?: boolean;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const PortalOptionPositionTable = (props: PortalOptionPositionTableProps) => {
    const { isHistoric = false } = props;

    const lang = useLanguage();
    const currentAccount = useAppSelector(selectCurrentAccount);

    const [stateLoading, setStateLoading] = useState<boolean>(false);

    const { debouncedTableInstance, columnSizing, sorting, formattedPortalPositions, expandedRows, globalFilter } =
        usePortalOptionPositionTable(props);

    useDidUpdate(() => {
        setStateLoading(true);
        setTimeout(() => setStateLoading(false), 1000);
    }, [currentAccount]);

    return (
        <div className="flex flex-col gap-1 bg-brand-background-dark p-4 sm:p-6 py-4 pb-6 border border-brand-neutral-700 text-brand-text rounded-md overflow-hidden max-h-[750px]">
            <div className="flex flex-col h-full gap-4">
                <div className="flex flex-wrap gap-2 justify-between">
                    <div className="">
                        {isHistoric ? lang.portal.historicOptionPositions : lang.portal.currentOptionPositions}
                    </div>
                    <div className="flex items-center justify-between gap-2 flex-wrap-reverse">
                        <div className="flex gap-2 whitespace-nowrap">
                            <div className="relative flex items-center">
                                <MdSearch className="absolute left-2 text-brand-neutral-400 z-10 w-5 h-5" />
                                <SafeInput
                                    className="w-48 text-xs rounded-md pl-8"
                                    placeholder="Search..."
                                    value={globalFilter || ''}
                                    onChange={(e) => debouncedTableInstance.setGlobalFilter(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {stateLoading ? (
                    <div className="m-2 mt-0">
                        <Loader />
                    </div>
                ) : (
                    <MemoizedPortalOptionPositionTable
                        expandedRows={expandedRows}
                        globalFilter={globalFilter}
                        tableInstance={debouncedTableInstance}
                        columnSizing={columnSizing}
                        sorting={sorting}
                        formattedPortalPositions={formattedPortalPositions}
                    />
                )}
            </div>
        </div>
    );
};

export default PortalOptionPositionTable;

const MemoizedPortalOptionPositionTable = memo(({ tableInstance }: any) => {
    // globalFilterEnabled
    return <PortalVirtualizedTable tableInstance={tableInstance} isLoadingTableContent={false} />;
});

MemoizedPortalOptionPositionTable.displayName = 'PortalOptionPositionTable';
